import { Box, BoxProps, ColorOptions, Image, Text } from '@jfs/ui-new'
import { isIE } from '@jfs/utils'
import { FC, useState } from 'react'
import { colors } from '../../util/colors'
import useFontSize from '../../util/useFontSize'
import usePadding from '../../util/usePadding'
import { isMobileWidth, isTabletWidth, useWindowDimensions } from '../../util/util'
import ContactForm from '../ContactForm'
import Footer from '../Footer'
import DottedImage, { dottedImageTypes } from '../ImageDotted'
import { Modal, ModalTypes } from '../ModalWeb'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import Navbar from '../Nav'
import NavMob from '../NavMobile'
import { SvgImage } from '../SvgImages'

const BgContact = require('../../assets/images/white-tail-blue-arrow.png')

interface ContactUsProps {
  type?: 'screen' | 'footer'
  footerNote?: string
}

const ContactUs: FC<ContactUsProps> = ({ type = 'screen', footerNote }: ContactUsProps) => {
  const viewPortWidth = useWindowDimensions()
  const [navVisible, setNavVisible] = useState(false)

  const handleNav = (): void => {
    document.body.style.overflow = 'hidden' // body scroll disabled
    setNavVisible((prev) => !prev)
  }

  const setVisiblity = (): void => {
    document.body.style.overflow = 'scroll' // body scroll disabled
    setNavVisible(false)
  }

  const nav = (
    <Box>
      <NavMob onPress={setVisiblity} />
    </Box>
  )

  const resPadding = usePadding()
  const themedStyle = styles(viewPortWidth, resPadding)

  const isTab = viewPortWidth < 1024
  const borderRadius = type === 'footer' ? (isTab ? 32 : 120) : 0
  return (
    <>
      <Modal view={nav} visible={navVisible} onPress={setVisiblity} type={ModalTypes.NAVBAR_RIGHT} />
      {type === 'screen' && <Navbar onPress={handleNav} isBg={true} hamBurgerType="primary" />}
      <Box {...themedStyle.outerontainer} borderTopRightRadius={borderRadius} borderTopLeftRadius={borderRadius}>
        <FormBox type={type} footerNote={footerNote} />
        <Box {...themedStyle.footerBackground}>
          <Footer />
        </Box>
      </Box>
    </>
  )
}

interface RenderCircle {
  width: number
  type?: string
}

const RenderCircle = ({ width, type }: RenderCircle) => {
  const isMobile = isMobileWidth(width)
  const isTab = width >= 600 && width <= 1024
  const isLargeDesktop = width > 1550

  return (
    <Box
      width={isMobile ? '100%' : '50%'}
      minWidth={isTab ? undefined : 300}
      maxWidth={isTab ? 320 : undefined}
      minHeight={isMobile ? undefined : '40vh'}
      justifyContent="flex-start"
      mt={isMobile ? 40 : 40}>
      <Box
        top={isMobile ? -50 : isTab ? -140 : -70}
        left={isMobile ? '-16%' : isTab ? '0%' : '-20%'}
        width={isMobile ? width : isLargeDesktop ? width / 2.5 : width / 2}
        maxWidth={isTab ? 320 : undefined}
        minHeight={isMobile ? 240 : 300}
        zIndex={99}
        overflow="visible"
        height={isMobile || isTab ? undefined : '70%'}>
        <Image
          source={BgContact}
          height={isMobile ? '100%' : '100%'}
          width={isMobile ? '90%' : isTab ? '100%' : '100%'}
          resizeMode={isMobile ? 'cover' : 'contain'}
        />
      </Box>
      <Box position="absolute" top={isMobile || isTab ? 0 : 108} alignSelf={isMobile ? 'center' : 'center'}>
        <SvgImage imageName={'Circle'} height={isMobile ? 170 : isTab ? 200 : 330} outerCircleDirection="right" />
      </Box>
      <Box
        zIndex={999}
        position={'absolute'}
        top={isMobile || isTab ? 20 : 225}
        alignSelf="flex-start"
        justify={isMobile ? 'center' : 'flex-start'}
        mt={!isTab && type === 'screen' ? undefined : 0}
        ph={isMobile ? 16 : 0}>
        <Box maxWidth={isMobile ? '80%' : isIE() ? 480 : undefined}>
          <Text
            lineHeight={useFontSize().circleHeadingLineHeight}
            fontSize={useFontSize().circleHeading}
            color="WHITE"
            fontFamily={'jiotype-black'}>
            {type === 'screen' && !isTab
              ? 'Contact the JioPay team'
              : 'Want to better your checkout experience? \nContact Us.'}
          </Text>
        </Box>
        <ModBox show={type === 'screen' && !isTab} maxWidth={isMobile ? 300 : 500} pt={isMobile || isTab ? 16 : 30}>
          <Text
            fontSize={isMobile ? 14 : 16}
            lineHeight={isMobile ? 24 : 28}
            color="WHITE"
            fontFamily={'jiotype-medium'}>
            {
              "We're glad to have you here and will answer any questions you have. Share your information with us and you'll hear from us shortly"
            }
          </Text>
        </ModBox>
      </Box>
    </Box>
  )
}

interface FormProps {
  type?: string
  footerNote?: string
}
const FormBox: FC<FormProps> = ({ type, footerNote }: FormProps) => {
  const viewPortWidth = useWindowDimensions()

  const isMobile = viewPortWidth < 600
  const isTablet = isTabletWidth(viewPortWidth)
  const isMobileOrTablet = isMobile || isTablet
  const resPadding = usePadding()
  const themedStyle = styles(viewPortWidth, resPadding)

  return (
    <Box bg={colors.JIO_PRIMARY_ALPHA as ColorOptions}>
      <Box
        {...themedStyle.formBox}
        flexDirection={isMobile ? 'column' : 'row'}
        pb={isMobile ? 0 : footerNote ? 60 : 170}>
        <RenderCircle width={viewPortWidth} type={type} />
        <Box
          width={isMobile ? '100%' : '50%'}
          alignItems="flex-end"
          minWidth={isMobile ? 220 : 300}
          pt={isMobile ? 0 : 20}
          pb={isMobile ? (footerNote ? 108 : 180) : 100}>
          <Box>
            <Box {...themedStyle.borderBox} />
            <Box zIndex={-99} left={isMobileOrTablet ? 30 : 140} width={'100%'} top={isMobileOrTablet ? -72 : -90}>
              <DottedImage
                opacity={0.4}
                position={dottedImageTypes.topRight}
                isDark={true}
                size={isMobileOrTablet ? 150 : undefined}
              />
            </Box>
            <Box>
              <ContactForm />
            </Box>
            <Box zIndex={-99} left={isMobileOrTablet ? -30 : -170} width={'100%'} top={100}>
              <DottedImage
                position={dottedImageTypes.bottomLeft}
                isDark={true}
                opacity={0.4}
                size={isMobileOrTablet ? 150 : undefined}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <ModBox show={!!footerNote} justify={'center'} pb={isMobile ? 120 : 170} ph={isMobile ? 4 : undefined}>
        <Text textAlign="center" fontSize={12} fontFamily={'jiotype-medium'} fontStyle={'italic'} color={'WHITE'}>
          {footerNote}
        </Text>
      </ModBox>
    </Box>
  )
}

const styles = (breakpoint: number, responsivePadding: number) => {
  // const responsivePadding = getPadding(breakpoint)
  const isIpad = isTabletWidth(breakpoint)
  const isMobile = isMobileWidth(breakpoint)
  return {
    outerontainer: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'scroll',
      bg: 'WHITE',
    } as BoxProps,
    leftBox: {
      display: 'flex',
      flex: 1,
      width: '50%',
      minWidth: 300,
    } as BoxProps,
    buttonContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      flex: 1,
      justifyContent: breakpoint > 600 ? 'flex-start' : 'space-between',
      minHeight: 120,
    } as BoxProps,
    footerBackground: {
      marginTop: isMobile ? -80 : -140,
      bg: 'WHITE',
      borderTopLeftRadius: breakpoint > 1024 ? 120 : 32,
      borderTopRightRadius: breakpoint > 1024 ? 120 : 32,
    } as BoxProps,
    formBox: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: isIpad || isMobile ? 'wrap' : 'nowrap',
      justifyContent: 'space-between',
      backgroundColor: colors.JIO_PRIMARY_ALPHA as ColorOptions,
      pl: responsivePadding as Number,
      pr: responsivePadding,
      pt: 80,
    } as BoxProps,
    borderBox: {
      borderRightWidth: 1,
      borderBottomWidth: 1,
      height: '98%',
      width: '100%',
      position: 'absolute',
      zIndex: -1,
      top: 26,
      left: 8,
      borderRightColor: 'BLUE',
      borderBottomColor: 'BLUE',
      borderRadius: 24,
    } as unknown as BoxProps,
  }
}

export default ContactUs
